import { artwork, type ArtImage } from '../assets';

/**
 * Full-bleed campaign key art.
 *
 * WebP first with a JPEG fallback, and deliberately **no AVIF** even though the
 * build machine can encode it. The kiosk is Android 11 on an RK3568, which has
 * no AV1 hardware decode — and a `<picture>` chain only protects us where AVIF
 * is unsupported. Where it is merely supported-and-slow the browser picks it
 * anyway and we eat a software decode on a Cortex-A55 mid-session.
 *
 * Widths are descriptors rather than a media query so the browser picks; it
 * caps at 1080 because that is the largest we ship. See `artwork` in assets.ts
 * for why there is no 2160.
 *
 * The blur-up sits underneath as a background image, so the first paint is the
 * art's own cream rather than a flash of the dark body behind it.
 */
export function Artwork({
  sources,
  alt = '',
  className = '',
  eager = false,
}: {
  /** Ascending by width. */
  sources: readonly ArtImage[];
  /** Empty by default: the art is decoration behind copy that already says it. */
  alt?: string;
  className?: string;
  /** Set on the first screen of a flow, where this is the largest paint. */
  eager?: boolean;
}) {
  const largest = sources[sources.length - 1];
  if (!largest) return null;

  const srcSet = (pick: (s: ArtImage) => string) =>
    sources.map((s) => `${pick(s)} ${s.width}w`).join(', ');

  return (
    <picture>
      <source type="image/webp" srcSet={srcSet((s) => s.webp)} sizes="100vw" />
      <img
        src={largest.fallback}
        srcSet={srcSet((s) => s.fallback)}
        sizes="100vw"
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        style={{ backgroundImage: `url(${artwork.lqip})`, backgroundSize: 'cover' }}
        className={`h-full w-full object-cover ${className}`}
      />
    </picture>
  );
}
